import { AUTHORITIES } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoClassZoomLink } from "@/shared/model/edo-class-zoom-link.model";
import { IEdoClass } from "@/shared/model/edo-class.model";
import { IEdoCourse } from "@/shared/model/edo-course.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getEntities } from "@/shared/reducers/entities/edo-course.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import {
  Avatar,
  IconButton,
  List,
  ListItem,
} from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { Video } from "lucide-react";
import { useEffect } from "react";
import { Translate } from "react-jhipster";
import { Link } from "react-router-dom";

interface CoursesGroupProps {
  class: IEdoClass;
  onOpenClassZoomLinksMenu?: (anchorEl: HTMLElement, classZoomLinks: IEdoClassZoomLink[]) => void;
}

const CoursesGroup = (props: CoursesGroupProps) => {
  const dispatch = useAppDispatch();
  const { entities: courses, loading } = useAppSelector<EntityState<IEdoCourse>>(
    (state) => state.edoCourse
  );
  const { account } = useAppSelector<AuthenticationState>((state) => state.authentication);
  const { class: edoClass } = props;

  useEffect(() => {
    const getCourses = async () => {
      const params = new URLSearchParams();
      if (
        !hasAnyAuthority(account?.roles || [], [AUTHORITIES.ROLE_STUDENT, AUTHORITIES.ROLE_SUPPORT_TEACHER]) &&
        hasAnyAuthority(account?.roles || [], [AUTHORITIES.ROLE_TEACHER])
      ) {
        params.append("filter.teachers.userId", "$eq:" + String(account?.id));
      }
      params.append("filter.class.id", "$eq:" + String(edoClass?.id));
      dispatch(
        getEntities({
          page: 0,
          size: 1000,
          sort: "id:ASC",
          filters: params.toString(),
        })
      );
    };
    if (edoClass && account?.id) getCourses();
  }, [edoClass, account?.id, dispatch]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <List className="flex-1 divide-y divide-[var(--color-mockup-border)]" style={{ padding: 0 }}>
      {courses?.length ? (
        courses.map((course) => (
          <Link key={course.id} to={`/courses/${course.id}`}>
            <ListItem className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-primary/5">
              <Avatar
                alt={course.subject?.name}
                fallback={course.subject?.name?.[0] ?? "?"}
                className="h-10 w-10 shrink-0 rounded-2xl bg-primary/10 text-primary"
              />
              <span className="min-w-0 flex-1 text-sm font-semibold text-[var(--color-mockup-text)]">
                {course.subject?.name}
              </span>
              {edoClass?.zoomInviteLinks?.length ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      aria-label="Link Zoom"
                      onClick={(e) => e.preventDefault()}
                      className="h-8 w-8 rounded-full text-[var(--color-mockup-text-light)] hover:bg-primary/10 hover:text-primary"
                    >
                      <Video className="h-4 w-4" />
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {edoClass.zoomInviteLinks.map((link) => (
                      <DropdownMenuItem
                        key={link.id}
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(link.zoomInviteLink, "_blank");
                        }}
                      >
                        {link.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </ListItem>
          </Link>
        ))
      ) : (
        <div className="px-6 py-12 text-center">
          <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
            <Translate contentKey="edocendoApp.edoCourse.home.notFound" />
          </p>
        </div>
      )}
    </List>
  );
};

export default CoursesGroup;
